import React, { useEffect, useRef, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "tools/routerCompat";
import { createUser, getAllRoles, forgotPassword } from "../../actions/user";
import { getMajorCompanies } from "actions/customer";
import Button from "@mui/material/Button";
import * as yup from "yup";
import MCard from "components/Shared/MCard.tsx";
import * as MCardDT from "typeConstants/dataTypes";
import Box from "@mui/material/Box";

const CreateUser = (props) => {
  const [createdUser, setCreatedUser] = useState({ loginEnabled: true });
  const [isUserCreatingAccount, setIsUserCreatingAccount] = useState(true);
  const cardControlMethods = useRef({});

  useEffect(() => {
    if (props.allRoles.length === 0 && !props.rolesList?.length) {
      props.getAllRoles();
    }
    if (!props.majorCompanies) {
      props.getMajorCompanies();
    }
    if (!props.isCreatingUser && Object.keys(props.internetUser).length !== 0 && !isUserCreatingAccount) {
      handleUserCreated();
    }
  }, [props.internetUser]);

  useEffect(() => {
    if (createdUser.primaryRole) cardControlMethods.current.refreshEditData();
  }, [createdUser]);

  const handleUserCreated = () => {
    props.forgotPassword(props.internetUser.username);
    props.history.push({
      pathname: "/user-profile",
      state: {
        internetUserId: props.internetUser.id,
      },
    });
  };

  const setCardControls = (cm) => {
    cardControlMethods.current = cm;
  };

  const handleEdit = (arr, obj) => {
    let newObj = { ...createdUser, ...obj };
    setCreatedUser(newObj);
  };

  const handleCreate = () => {
    if (cardControlMethods.current.runValidation(true)) props.createUser(createdUser);
    setIsUserCreatingAccount(false);
  };

  const getRoleOptions = () => {
    if (props.rolesList?.length) {
      return props.rolesList;
    } else {
      return props.allRoles;
    }
  };

  const majorCompanyOptions = props.majorCompanies
    ? props.majorCompanies.map((v) => ({ label: v.companyName, value: v.id }))
    : [];

  const requireMajorCompanyRoles = ["Customer", "Customer Admin", "ValuationCustomer", "ValuationCustomerManager"];

  const getCardFields = (createdUser) => {
    let fields = [
      {
        key: "username",
        label: "Username",
        validation: yup.string().required("Username is a required field"),
      },
      {
        key: "firstName",
        label: "First Name",
        validation: yup.string().required("First Name is a required field"),
      },
      {
        key: "lastName",
        label: "Last Name",
        validation: yup.string().required("Last Name is a required field"),
      },
      {
        key: "emailAddress",
        label: "Email",
        validation: yup.string().email("Email must be a valid email").required("Email is a required field"),
      },
      {
        key: "primaryRole",
        label: "Role",
        validation: yup.string().required("Role is a required field"),
        value: {
          value: createdUser.primaryRole,
          label: createdUser.primaryRole,
        },
        dataType: MCardDT.DATA_TYPE_DROPDOWN2,
        dataTypeOptions: {
          options: getRoleOptions(),
          nullable: false,
        },
      },
    ];
    if (requireMajorCompanyRoles.includes(createdUser.primaryRole)) {
      fields.push({
        key: "majorCompanyId",
        label: "Major Company",
        validation: yup.string().required("Major Company is a required field"),
        value: {
          value: createdUser.majorCompanyId,
          label: createdUser.majorCompany,
        },
        dataType: MCardDT.DATA_TYPE_DROPDOWN,
        dataTypeOptions: {
          options: majorCompanyOptions,
          nullable: false,
        },
      });
    }
    fields.push({
      key: "loginEnabled",
      label: "Enabled",
      dataType: MCardDT.DATA_TYPE_BOOLEAN,
      value: true,
    });
    return fields;
  };

  const memoizedCardFields = useMemo(
    () => getCardFields(createdUser, props.majorCompanies),
    [createdUser, props.majorCompanies],
  );

  return (
    <Box sx={{ padding: 1 }}>
      <MCard
        title="Create User"
        onlyEditable
        onEditFinished={handleEdit}
        getControlMethods={setCardControls}
        sx={{ height: "auto" }}
        fields={[
          ...memoizedCardFields,
          {
            key: "buttons",
            control: (
              <div className="flex-row-between" style={{ paddingTop: 10 }}>
                <Button onClick={handleCreate}>Create</Button>
                <Button color="secondary" onClick={() => props.onCancelClicked()}>
                  Cancel
                </Button>
              </div>
            ),
          },
        ]}
      />
    </Box>
  );
};

CreateUser.propTypes = {
  internetUser: PropTypes.object.isRequired,
  isCreatingUser: PropTypes.bool.isRequired,
  createUser: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  allRoles: PropTypes.array.isRequired,
  isFetchingAllRolesList: PropTypes.bool.isRequired,
  getAllRoles: PropTypes.func.isRequired,
  forgotPassword: PropTypes.func.isRequired,
  onCancelClicked: PropTypes.func.isRequired,
  rolesList: PropTypes.array,
  majorCompanies: PropTypes.array,
};
const mapStateToProps = (state) => {
  return {
    internetUser: state.user.internetUser,
    isCreatingUser: state.user.isCreatingUser,
    allRoles: state.user.allRoles,
    isFetchingAllRolesList: state.user.isFetchingAllRolesList,
    majorCompanies: state.customer.majorCompanies,
    isFetchingMajorCompanies: state.customer.isFetchingMajorCompanies,
  };
};
const mapDispatchToProps = (dispatch) => ({
  createUser(dto) {
    dispatch(createUser(dto));
  },
  getAllRoles() {
    dispatch(getAllRoles());
  },
  forgotPassword(username) {
    dispatch(forgotPassword(username));
  },
  getMajorCompanies() {
    dispatch(getMajorCompanies());
  },
});
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CreateUser));
